// Copyright (c) StrangeBee 2023

import { useGetSettings } from '@Queries/UISettingsQueries';
import RouteBuilder from '@Services/RouteBuilder';
import produce, { setAutoFreeze } from 'immer';
import { DEFAULT_DATE_FORMAT } from './useFormatDate';
import useLocalStorage, { LocalStorageKeys } from './useLocalStorage';

setAutoFreeze(false);

interface OptionsStorageType {
    homepage: string;
    defaultDateFormat: string;
}

interface UseStorageOptionsReturn {
    homepage: string;
    defaultDateFormat: string;
    setHomepage: (homepage: string) => void;
    setDefaultDateFormat: (dateFormat: string) => void;
    setOptions: (options: OptionsStorageType) => void;
}

export const useStorageOptions = (): UseStorageOptionsReturn => {
    const { data: settingsData } = useGetSettings();

    const { storage, setStorage } = useLocalStorage<OptionsStorageType>({
        key: LocalStorageKeys.OPTIONS,
        initialValue: {
            homepage: RouteBuilder.compute('cases.default'),
            defaultDateFormat: settingsData?.defaultDateFormat || DEFAULT_DATE_FORMAT,
        },
    });

    const setHomepage = (homepage: string) =>
        setStorage(
            produce<OptionsStorageType>((draft) => {
                draft.homepage = homepage;
            }),
        );

    const setDefaultDateFormat = (dateFormat: string) =>
        setStorage(
            produce<OptionsStorageType>((draft) => {
                draft.defaultDateFormat = dateFormat;
            }),
        );

    const setOptions = (options: OptionsStorageType) => setStorage(options);

    return {
        homepage: storage.homepage,
        defaultDateFormat: storage.defaultDateFormat,
        setHomepage,
        setDefaultDateFormat,
        setOptions,
    };
};
